import {
  CreateAssessmentRequest,
  PrepareForRulaVideoUploadRequest,
  PrepareForVideoUploadRequest,
  ErgonetPrepareForVideoUploadRequest,
  ErgonetPrepareForRulaVideoUploadRequest,
} from "../assessment/assessmentService";
import {
  DB_VERSION,
  DB_NAME,
  LOCAL_ASSESSMENTS_OBJECT_STORE,
} from "./constants";
import { AssessmentRecord } from "./types/assessmentRecord";
import store from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/constants/constants";
// This class handles offline storage of recorded videos. The goal is to be able to store the videos
// locally on device when there is no network connection available and later upload the videos
// when network connection is back.
// Related SIM: https://issues.amazon.com/issues/V931406873
// This leverages IndexedDB for storage purposes. The API here wraps the mechanism IndexedDB uses
//  within a promise based workflow so that it is easy to use.
class LocalAssessmentStoreService {
  db: IDBDatabase | null = null;

  async createOrRetrieveStore() {
    return await new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      // This callback is run whenever you create a new database or increase the version number of an
      // existing database. This is where the database structure can be changed. You'll create/delete
      // object stores and build/remove indices here.
      request.onupgradeneeded = (event) => {
        let openRequest: IDBOpenDBRequest = event.target as IDBOpenDBRequest;
        let db = openRequest.result;

        // if already created then don't create it. If you do, the the DB will not be opened successfully.
        // Refer to https://stackoverflow.com/questions/44002460/getting-object-store-already-exists-inside-onupgradeneeded
        // for the mechanism on creating new object stores or updating the existing one safely.
        if (!db.objectStoreNames.contains(LOCAL_ASSESSMENTS_OBJECT_STORE)) {
          const localAssessmentsObjectStore = db.createObjectStore(
            LOCAL_ASSESSMENTS_OBJECT_STORE,
            { keyPath: "id" }
          );
          localAssessmentsObjectStore.createIndex("createdAt", "createdAt", {
            unique: false,
          });
          localAssessmentsObjectStore.createIndex("videoBlob", "videoBlob", {
            unique: false,
          });
        }
      };

      // If the db does not exist (or if the version has changed), then this callback is run after
      // the onupgradeneeded one. If the db exists with the requested version, then this is run
      // when the db open request succeeds. A reference to the db can be assigned from within this
      // callback.
      request.onsuccess = (event) => {
        let openRequest: IDBOpenDBRequest = event.target as IDBOpenDBRequest;
        this.db = openRequest.result;
        resolve(this.db);
      };

      request.onerror = () => {
        store.dispatch(
          createToast({
            type: "error",
            message: `The DB could not be opened successfully. If the problem still persists, try clearing out your browser data for Ergona. You will lose any locally stored videos.`,
            timeout: TOAST_TIMEOUT,
          })
        );
      };
    });
  }

  async addEntryToStore(
    videoBlob: Blob,
    id: number,
    evaluationId: null | undefined | string,
    assessmentInfo: CreateAssessmentRequest,
    prepareForVideoUploadPayload:
      | PrepareForVideoUploadRequest
      | PrepareForRulaVideoUploadRequest
      | ErgonetPrepareForVideoUploadRequest
      | ErgonetPrepareForRulaVideoUploadRequest
  ) {
    await this.createOrRetrieveStore();
    return await new Promise(() => {
      if (this.db !== null) {
        this.db
          .transaction(LOCAL_ASSESSMENTS_OBJECT_STORE, "readwrite")
          .objectStore(LOCAL_ASSESSMENTS_OBJECT_STORE)
          .add({
            id: id,
            createdAt: new Date().getTime(),
            videoBlob: videoBlob,
            evaluationId: evaluationId,
            assessmentPayload: assessmentInfo,
            prepareForVideoUploadPayload: prepareForVideoUploadPayload,
          });
      }
    });
  }

  async getAllEntries(): Promise<AssessmentRecord[]> {
    await this.createOrRetrieveStore();
    return await new Promise((resolve) => {
      if (this.db !== null) {
        let transaction = this.db.transaction([LOCAL_ASSESSMENTS_OBJECT_STORE]);
        let objectStore = transaction.objectStore(
          LOCAL_ASSESSMENTS_OBJECT_STORE
        );
        let requestToGetAll = objectStore.getAll();

        requestToGetAll.onerror = function (event) {
          store.dispatch(
            createToast({
              type: "error",
              message: "Unable to retrieve data from local database",
              timeout: TOAST_TIMEOUT,
            })
          );
          return "";
        };

        requestToGetAll.onsuccess = function (event) {
          resolve(requestToGetAll.result);
        };
      }
    });
  }

  async removeEntry(keyId: number): Promise<AssessmentRecord[]> {
    await this.createOrRetrieveStore();
    return await new Promise((resolve) => {
      if (this.db !== null) {
        let transaction = this.db.transaction(
          [LOCAL_ASSESSMENTS_OBJECT_STORE],
          "readwrite"
        );
        let objectStore = transaction.objectStore(
          LOCAL_ASSESSMENTS_OBJECT_STORE
        );
        let requestToDeleteEntry = objectStore.delete(keyId);

        requestToDeleteEntry.onerror = (event) => {
          store.dispatch(
            createToast({
              type: "error",
              message: "Unable to delete data from local database",
              timeout: TOAST_TIMEOUT,
            })
          );
          return "";
        };

        requestToDeleteEntry.onsuccess = async (event) => {
          // fetch the other entries
          resolve(await this.getAllEntries());
        };
      }
    });
  }
}

export default new LocalAssessmentStoreService();
